import { WeightClassResponseDto } from '@planning/app/api'
import { EventTypes } from '@planning/messages/eventsTypes'
import { GetWeightClassesQuery } from '@planning/messages/queries/getWeightClassesQueryHandler'
import { getBaseContainerLength, IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { ItemStore } from '../base/ItemStore'
import { IEntityStore } from '../types'

export class WeightClassItem extends EntityItem<WeightClassResponseDto, string> {}

export class WeightClassItemStore
  extends ItemStore<WeightClassResponseDto, WeightClassItem, string>
  implements IEntityStore<WeightClassItem>
{
  private hasBeenLoaded = false

  constructor(private messageBus: IMessageBus) {
    super((key, data) => new WeightClassItem(key, data))
    makeObservable(this, {
      activeWeightClasses: computed,
      receiveQuery: action,
      receiveUpsertedEvent: action,
      receiveDeletedEvent: action,
    })

    messageBus.subscribeEvent(GetWeightClassesQuery.type, this.receiveQuery)
    messageBus.subscribeEvent(
      EventTypes.WeightClassUpsertedInternalEvent,
      this.receiveUpsertedEvent,
    )
    messageBus.subscribeEvent(EventTypes.WeightClassDeletedInternalEvent, this.receiveDeletedEvent)
  }

  fetch = async () => {
    if (this.hasBeenLoaded) return
    this.hasBeenLoaded = true
    await this.messageBus.dispatchQuery(new GetWeightClassesQuery())
  }

  get activeWeightClasses() {
    return _(this.elements)
      .map(item => item.data)
      .filter(item => item.weightClassSetIsActive === true)
      .orderBy(item => item.minWeight)
      .value()
  }

  getWeightClass = (
    weight: number,
    containerLength: number,
    vesselServiceId?: number | null,
  ): WeightClassResponseDto | undefined => {
    const vesselServiceIdToSearch = this.activeWeightClasses.some(
      wc => wc.weightClassSetVesselServiceId === vesselServiceId,
    )
      ? vesselServiceId
      : undefined

    const containerLengthFinal = getBaseContainerLength(containerLength)

    return this.activeWeightClasses.find(wc => {
      const wcVesselServiceId = wc.weightClassSetVesselServiceId ?? undefined
      const isFromVesselService = wcVesselServiceId === vesselServiceIdToSearch

      const isTheSameSize =
        !wc.weightClassSetContainerLength ||
        wc.weightClassSetContainerLength === containerLengthFinal

      const isAboveMin = weight >= wc.minWeight!

      const isBelowMax = !wc.maxWeight || weight <= wc.maxWeight

      return isFromVesselService && isTheSameSize && isAboveMin && isBelowMax
    })
  }

  receiveQuery = (event: IEvent<WeightClassResponseDto[]>): void => {
    if (event.payload) {
      this.upsertBulk(event.payload)
    }
  }

  receiveUpsertedEvent = (event: IEvent<WeightClassResponseDto>) => {
    if (event.payload) {
      this.upsert(event.payload)
    }
  }

  receiveDeletedEvent = (event: IEvent<string>): void => {
    if (event.payload) {
      this.delete(event.payload)
    }
  }
}
